import React, { useContext, useEffect, useLayoutEffect, useState } from 'react';
import {
	Accordion,
	AccordionSummary,
	AccordionDetails,
	Box,
	IconButton,
	List,
	ListItem,
	ListItemIcon,
	ListItemText,
	SwipeableDrawer,
	Typography,
	Button,
	ListItemSecondaryAction,
	Icon,
	useTheme,
	useMediaQuery,
	styled
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { animateScroll } from 'react-scroll';
import { MenuItem, OnlineStatusAvatar, UserMenu, Logo } from 'components';
import { AppContext } from 'context';
import { useAuth, useMenu } from 'hooks';
import { MENU_ITEMS, NO_AUTH_ROUTES } from 'lib/navigation';

const drawerWidth = 300;

const CustomAccordion = styled(Accordion)({
	border: 'none',
	boxShadow: 'none',
	'&:before': {
		display: 'none'
	}
});

const Drawer = ({ open, toggleMenu, ...props }) => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));
	const navigate = useNavigate();
	const { pathname } = useLocation();
	const { authenticated, currentUser } = useContext(AppContext);
	const { fetchMe, logoutUser } = useAuth();
	const { open: openMenu, anchorEl, closeMenu, handleToggleMenu } = useMenu();

	const handleLogoutClick = () => logoutUser();

	const handleClick = (url) => {
		closeMenu();
		if (isMobile && typeof toggleMenu === 'function') {
			toggleMenu();
		}
		animateScroll.scrollToTop();
		navigate(url);
	};

	useEffect(() => {
		if (!currentUser && !NO_AUTH_ROUTES.includes(pathname)) {
			fetchMe();
		}
	}, [authenticated]);

	return (
		<SwipeableDrawer
			open={open}
			variant={isMobile ? 'temporary' : 'permanent'}
			onClose={() =>
				isMobile && typeof toggleMenu === 'function' && toggleMenu()
			}
			disableSwipeToOpen={!isMobile}
			sx={{
				'& .MuiDrawer-paper': {
					width: drawerWidth,
					boxSizing: 'border-box',
					border: 'none',
					backgroundColor: theme.palette.secondary.main
				}
			}}
			{...props}
		>
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					flexDirection: 'column',
					height: '100%'
				}}
			>
				<List
					component="nav"
					sx={{
						backgroundColor: theme.palette.secondary.main,
						width: drawerWidth,
						height: '100%',
						padding: 0
					}}
				>
					<Box
						sx={{
							p: 4,
							width: '100%',
							backgroundColor: theme.palette.secondary.main,
							display: 'flex',
							justifyContent: 'center'
						}}
					>
						<Logo />
					</Box>
					{MENU_ITEMS[currentUser ? 'loggedIn' : 'loggedOut'].map(
						(category, idx) =>
							category.title === 'Auth' ? (
								category.items.map((item, i) => (
									<MenuItem
										key={i}
										active={pathname === item.value}
										value={item.value}
										text={item.text}
										icon={item.icon}
										handleClick={() => handleClick(item.value)}
										sx={{ paddingLeft: theme.spacing(3) }}
									/>
								))
							) : (
								<CustomAccordion
									key={idx}
									slotProps={{ transition: { unmountOnExit: true } }}
									sx={{
										backgroundColor: theme.palette.secondary.main,
										width: drawerWidth,
										padding: 0
									}}
								>
									<AccordionSummary
										expandIcon={<ExpandMore sx={{ color: 'white' }} />}
										sx={{
											marginTop: '-5px',
											marginBottom: '-5px'
										}}
									>
										<ListItem
											variant="h6"
											sx={{
												color: 'white',
												minHeight: theme.spacing(4),
												padding: theme.spacing(0, 1, 0, 0),
												margin: 0,
												'& .MuiAccordionSummary-content': {
													margin: 0
												}
											}}
										>
											{category.icon && (
												<ListItemIcon
													sx={{
														color: 'white',
														fontSize: 20,
														paddingRight: theme.spacing(1),
														minHeight: 32
													}}
												>
													<category.icon />
												</ListItemIcon>
											)}
											<ListItemText
												primary={category.title}
												primaryTypographyProps={{
													sx: { color: '#ffffff' }
												}}
											/>
										</ListItem>
									</AccordionSummary>
									<AccordionDetails
										sx={{
											marginTop: '-35px',
											marginBottom: '-40px'
										}}
									>
										<List component="nav">
											{category.items.map((item, i) => (
												<MenuItem
													key={i}
													active={pathname === item.value}
													value={item.value}
													text={item.text}
													icon={item.icon}
													handleClick={() => handleClick(item.value)}
													sx={{ paddingLeft: theme.spacing(3) }}
												/>
											))}
										</List>
									</AccordionDetails>
								</CustomAccordion>
							)
					)}
				</List>
			</Box>
			{currentUser && (
				<List
					sx={{
						backgroundColor: theme.palette.secondary.main,
						color: theme.palette.secondary.contrastText,
						paddingTop: theme.spacing(0.1)
					}}
				>
					<ListItem>
						<ListItemIcon>
							<OnlineStatusAvatar online={true} user={currentUser} />
						</ListItemIcon>
						<ListItemText
							primary={
								<Typography variant="h5" sx={{ color: 'white' }}>
									{currentUser.name}
								</Typography>
							}
							secondary={
								<Typography variant="body2" sx={{ color: 'white' }}>
									{currentUser.role}
								</Typography>
							}
						/>
						<ListItemSecondaryAction>
							<IconButton onClick={handleToggleMenu}>
								<ExpandMore
									sx={{
										color: 'white',
										fontSize: 20,
										paddingRight: theme.spacing(1)
									}}
								/>
							</IconButton>
							<UserMenu
								open={openMenu}
								anchorEl={anchorEl}
								handleToggleMenu={handleToggleMenu}
								handleLogoutClick={handleLogoutClick}
								handleClick={handleClick}
							/>
						</ListItemSecondaryAction>
					</ListItem>
				</List>
			)}
		</SwipeableDrawer>
	);
};

export default Drawer;
